import { Builder } from '@builder.io/react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { isBrowser } from '../../utils';

export type Variant = { name: string; percent: number; control: boolean };
export type VariantRegistration = { [variant: string]: boolean };

export type Props = {
  children: ReactNode;
  variants: Variant[];
  experimentName: string;
  showHelperModal: boolean;
};

type ActiveVariant = {
  name: string;
  // If the test was set up incorrectly, then we fallback to the control or the first named variant.
  isFallback: boolean;
};

const getStoredVariant = (experimentName: string) => ({
  name:
    (isBrowser() && window.localStorage.getItem(`pb_${experimentName}`)) || '',
  isFallback: false,
});

const setStoredVariant = (experimentName: string, variantName: string) => {
  if (isBrowser()) window.localStorage[`pb_${experimentName}`] = variantName;
};

export function ABTest({ variants = [], experimentName, children }: Props) {
  const [activeVariant, setActiveVariant] = useState<ActiveVariant>(
    getStoredVariant(experimentName),
  );

  // Used to track whether variants have been added as children of ABTest
  const [registration, setRegistration] = useState<VariantRegistration>({});

  useEffect(() => {
    const isPreviewMode = Builder.isEditing || Builder.isPreviewing;
    const haveChildrenRegistered = !!Object.keys(registration).length;
    if (
      activeVariant?.name ||
      !variants ||
      isPreviewMode ||
      !haveChildrenRegistered
    )
      return;
    const arePrecentagesCorrect =
      variants?.reduce((acc, curr) => acc + curr?.percent, 0) === 100;

    const isEveryVariantNamedAndRegistered = variants.every(
      (variant) => variant?.name?.trim() !== '' && registration[variant?.name],
    );

    const shouldUseFallbackVariant =
      !experimentName ||
      !arePrecentagesCorrect ||
      !isEveryVariantNamedAndRegistered;

    // If there is a problem with how the test was set up, then we use the fallback variant.
    if (shouldUseFallbackVariant) {
      const control = variants.find(
        (variant) =>
          variant.control &&
          variant.name.trim() !== '' &&
          registration[variant.name],
      );

      const firstNamedVariant = variants.find(
        (variant) => variant.name.trim() !== '' && registration[variant.name],
      );

      /**
       * If there is a control, then we use that,
       * otherwise we use the first named variant.
       * If there is no control and no variants have names, then god help us all.
       */
      const fallbackVariantName =
        control?.name || firstNamedVariant?.name || '';

      setActiveVariant({
        name: fallbackVariantName,
        isFallback: true,
      });
    } else {
      const randomizedVariant = getRandomizedVariant(variants);
      setStoredVariant(experimentName, randomizedVariant);
      setActiveVariant({ name: randomizedVariant, isFallback: false });
    }
  }, [variants, experimentName, registration, activeVariant?.name]);

  const registerVariant = useCallback((variant) => {
    setRegistration((prevState) => {
      return { ...prevState, [variant]: true };
    });
  }, []);

  const deregisterVariant = useCallback((variant) => {
    setRegistration((prevState) => {
      return { ...prevState, [variant]: false };
    });
  }, []);

  return (
    <ABTestContext.Provider
      value={{
        activeVariant,
        experimentName,
        registerVariant,
        deregisterVariant,
      }}
    >
      {children}
    </ABTestContext.Provider>
  );
}

const ABTestContext = createContext({
  activeVariant: { name: '', isFallback: false },
  experimentName: '',
  registerVariant: (_variant: string) => {},
  deregisterVariant: (_variant: string) => {},
});

export const useABTestContext = () => {
  const context = useContext(ABTestContext);
  return context;
};

const getRandomizedVariant = (variants: Variant[]) => {
  const random = Math.random() * 100;
  const { variant } = variants.reduce(
    (acc, curr) => {
      const min = acc.remaining - curr.percent;
      const max = acc.remaining;
      if (random >= min && random < max)
        return { remaining: min, variant: curr.name };
      return { ...acc, remaining: min };
    },
    {
      remaining: 100,
      variant: '',
    },
  );
  return variant;
};
